import { AlertTypes } from '../types';

// Mirrors node-api's FALSE_ALARM_DISPOSITIONS (alertService.js). Alerts that
// predate the isFalseAlarm hotfix can carry an INCIDENT DISPOSITION trail
// entry with one of these messages but no isFalseAlarm flag, so the message
// check can't be replaced by the flag alone.
const FALSE_ALARM_DISPOSITIONS = new Set([
    'SUBSCRIBER TESTING',
    'FALSE ALARM / ACCIDENTAL ACTIVATION',
]);

// INCIDENT DISPOSITION is RapidSOS's terminal operator disposition. node-api
// relabels false-alarm dispositions to SYSTEM PROCESSING COMPLETE before they
// reach the trail, so a surviving INCIDENT DISPOSITION entry means emergency
// services were dispatched for a real incident. Returns the operator's
// disposition text (e.g. "DISPATCHED - EMS"), or null when there is none.
export const getDispatchDisposition = (
    alert?: Partial<AlertTypes.Model> | null
): string | null => {
    if (!alert || alert.isFalseAlarm === true) return null;
    // dispatchTrail is newest-first (node-api unshifts entries).
    const entry = (alert.dispatchTrail ?? []).find(
        (e) =>
            (e?.dispatchStatus ?? '').toUpperCase() === 'INCIDENT DISPOSITION' &&
            !FALSE_ALARM_DISPOSITIONS.has((e?.message ?? '').toUpperCase())
    );
    if (entry) return entry.message || 'DISPATCHED';
    // A real dispatch can also arrive with NO terminal operator disposition
    // (first seen in prod 2026-09-02): the monitoring center emits DISPATCHED
    // AUTHORITY ("MONITORING CENTER HAS DISPATCHED FOR THE ALARM") and the
    // RapidSOS flow ends without an INCIDENT DISPOSITION ever being recorded.
    // Treat that entry as a dispatched signal with a fallback label so an alert
    // where emergency services were actually sent never renders as a plain
    // green "Resolved".
    const dispatchedAuthority = (alert.dispatchTrail ?? []).some(
        (e) => (e?.dispatchStatus ?? '').toUpperCase() === 'DISPATCHED AUTHORITY'
    );
    return dispatchedAuthority ? '911 Activated' : null;
};

export const hasDispatchDisposition = (
    alert?: Partial<AlertTypes.Model> | null
): boolean => getDispatchDisposition(alert) !== null;

// The CSOC canceled the alarm mid-flow (e.g. member gave the passcode)
// without recording a disposition. Only this exact message occurs in prod;
// it can coexist with SUBSCRIBER TESTING on flagged false alarms and with a
// real disposition on dispatched alerts, so both of those outrank it.
const MONITORING_CENTER_CANCELED = 'MONITORING CENTER HAS CANCELED THE ALARM';

export const isMonitoringCenterCanceled = (
    alert?: Partial<AlertTypes.Model> | null
): boolean => {
    if (!alert || alert.isFalseAlarm === true) return false;
    if (hasDispatchDisposition(alert)) return false;
    return (alert.dispatchTrail ?? []).some(
        (e) => (e?.message ?? '').toUpperCase() === MONITORING_CENTER_CANCELED
    );
};
